import { useState } from 'react'
import { AlertTriangle, Circle, PackageCheck, Truck } from 'lucide-react'
import type { ShipmentEvent, ShipmentStatus } from '@/types'
import { formatDateTime } from '@/lib/date'
import { isShipmentActive } from '@/lib/shipment'
import { cn } from '@/lib/cn'

/** The public tracker's subset is assignable here; the optional keys simply stay undefined. */
export type TimelineEvent = Pick<ShipmentEvent, 'name' | 'at' | 'office' | 'reason' | 'proof_image_urls'> & Partial<ShipmentEvent>

/**
 * The courier's scans, newest first as the API sends them. Shared by the
 * admin order page, the customer's own order page and the public tracker,
 * with `variant` deciding what is shown: remarks are courier-internal wording
 * and never reach the storefront.
 *
 * Collapses to the latest few on every width, not only mobile - a delivered
 * parcel routinely has 8 to 12 scans that nobody wants in full on desktop.
 */
export default function ShipmentTimeline({
  events,
  status,
  variant = 'portal',
  initialCount = 3,
  className,
}: {
  events: TimelineEvent[]
  status: ShipmentStatus
  variant?: 'portal' | 'storefront'
  initialCount?: number
  className?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const storefront = variant === 'storefront'
  const collapsible = events.length > initialCount + 1
  const shown = collapsible && !expanded ? events.slice(0, initialCount) : events

  if (events.length === 0) {
    return (
      <p className={cn('rounded-lg bg-app p-2.5 text-xs text-muted', className)}>
        {storefront
          ? 'Your parcel has been booked. Updates appear here once the courier scans it.'
          : 'Waiting for the first scan from the courier.'}
      </p>
    )
  }

  const danger = storefront ? 'text-danger' : 'text-status-danger'
  const success = storefront ? 'text-success' : 'text-status-success'
  const moving = storefront ? 'text-primary' : 'text-status-processing'

  return (
    <div className={className}>
      <ol className="space-y-3">
        {shown.map((event, index) => {
          const isNewest = index === 0
          const Icon = event.reason
            ? AlertTriangle
            : event.proof_image_urls.length > 0
              ? PackageCheck
              : isNewest && isShipmentActive({ status })
                ? Truck
                : Circle
          const tone = event.reason ? danger : event.proof_image_urls.length > 0 ? success : isNewest && isShipmentActive({ status }) ? moving : 'text-muted'

          return (
            <li key={`${event.at}-${event.name}-${index}`} className="flex items-start gap-3">
              <Icon className={cn('mt-0.5 size-4 shrink-0', tone)} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{event.name}</p>
                <p className="text-xs text-muted">
                  {formatDateTime(event.at)}
                  {event.office ? ` - ${event.office}` : ''}
                </p>
                {event.reason && <p className={cn('text-xs font-semibold break-words', danger)}>{event.reason}</p>}
                {!storefront && event.remark && <p className="text-xs text-muted break-words">{event.remark}</p>}
                {event.proof_image_urls.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {event.proof_image_urls.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer">
                        <img src={url} alt="Proof of delivery" className="size-16 rounded-lg border border-foreground/15 object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ol>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-2 text-xs font-semibold text-primary hover:underline"
        >
          {expanded ? 'Show fewer' : `Show all ${events.length} updates`}
        </button>
      )}
    </div>
  )
}
